import {
  Alert,
  Card,
  Col,
  DataTable,
  DetailDescriptions,
  DetailDrawer,
  DownloadButton,
  Empty,
  Image,
  Row,
  Space,
  Spin,
  Statistic,
  StatusTag,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  ViewButton,
} from '@wanjianshi/admin-ui';
import type { ApiResponse, PageResult } from '@wanjianshi/shared-contracts';
import { ApiError } from '@wanjianshi/shared-http';
import { formatDateTime, formatNumber } from '@wanjianshi/shared-utils';
import { FileOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminPage } from '@/hooks/useAdminResource';
import { adminApi } from '@/services/api/client';
import type {
  ProjectAssetRow,
  ProjectCanvasConnectionRow,
  ProjectCanvasDetail,
  ProjectCanvasGroupRow,
  ProjectCanvasNodeRow,
  ProjectCanvasRow,
} from './projectDetail';

const assetKindLabels: Record<string, string> = {
  image: '图片',
  video: '视频',
  audio: '音频',
  music: '音乐',
  text: '文本',
  document: '文档',
  file: '文件',
};

function textValue(value: unknown): string {
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

function jsonText(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return String(value ?? '');
  }
}

function normalizedAssetType(asset: Pick<ProjectAssetRow, 'kind' | 'uri'>): string {
  const value = `${asset.kind} ${asset.uri}`.toLowerCase();
  if (/image|\.png(?:\?|$)|\.jpe?g(?:\?|$)|\.webp(?:\?|$)|\.gif(?:\?|$)|\.svg(?:\?|$)/.test(value)) return 'image';
  if (/video|\.mp4(?:\?|$)|\.webm(?:\?|$)|\.mov(?:\?|$)|\.m3u8(?:\?|$)/.test(value)) return 'video';
  if (/audio|music|\.mp3(?:\?|$)|\.wav(?:\?|$)|\.m4a(?:\?|$)|\.aac(?:\?|$)|\.flac(?:\?|$)/.test(value)) return 'audio';
  return 'file';
}

function AssetPreview({ asset, detailed = false }: { asset: ProjectAssetRow; detailed?: boolean }) {
  const type = normalizedAssetType(asset);
  if (type === 'image') {
    return (
      <Image
        src={asset.uri}
        alt={asset.kind}
        className={detailed ? 'project-asset-preview__image--detail' : 'project-asset-preview__image'}
        fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='100'%3E%3Crect width='100%25' height='100%25' fill='%23f2f3f5'/%3E%3Ctext x='50%25' y='52%25' text-anchor='middle' fill='%238f959e' font-size='13'%3E%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5%3C/text%3E%3C/svg%3E"
      />
    );
  }
  if (type === 'video') {
    return (
      <video
        className={detailed ? 'project-asset-preview__video--detail' : 'project-asset-preview__video'}
        src={asset.uri}
        controls={detailed}
        muted={!detailed}
        preload="metadata"
      />
    );
  }
  if (type === 'audio' && detailed) {
    return <audio className="project-asset-preview__audio" src={asset.uri} controls preload="metadata" />;
  }
  return (
    <div className={detailed ? 'project-asset-preview__file--detail' : 'project-asset-preview__file'}>
      <FileOutlined />
      <span>{type === 'audio' ? '音频文件' : '文件资产'}</span>
    </div>
  );
}

function PanelError({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  return error ? <Alert showIcon type="error" message={error} action={<a onClick={() => void onRetry()}>重试</a>} /> : null;
}

async function fetchAllAdminPages<T>(path: string): Promise<T[]> {
  const pageSize = 500;
  const first = await adminApi.get<ApiResponse<PageResult<T>>>(path, {
    params: { page: 1, page_size: pageSize },
  });
  const items = [...(first.data.items ?? [])];
  const total = first.data.total ?? items.length;
  for (let page = 2; items.length < total; page += 1) {
    const response = await adminApi.get<ApiResponse<PageResult<T>>>(path, {
      params: { page, page_size: pageSize },
    });
    const nextItems = response.data.items ?? [];
    if (!nextItems.length) break;
    items.push(...nextItems);
  }
  return items;
}

export function ProjectAssetsPanel({
  projectId,
  kindCounts,
}: {
  projectId: number;
  kindCounts: Record<string, number>;
}) {
  const resource = useAdminPage<ProjectAssetRow>(`/admin/projects/${projectId}/assets`, 20);
  const [detail, setDetail] = useState<ProjectAssetRow | null>(null);
  const kindSummary = Object.entries(kindCounts).sort((left, right) => right[1] - left[1]);

  const columns = useMemo<TableColumnsType<ProjectAssetRow>>(() => [
    {
      title: '预览',
      key: 'preview',
      fixed: 'left',
      width: 150,
      render: (_value, row) => <div className="project-asset-preview"><AssetPreview asset={row} /></div>,
    },
    {
      title: '资产类型',
      dataIndex: 'kind',
      key: 'kind',
      width: 110,
      render: value => <Tag color="blue">{assetKindLabels[String(value).toLowerCase()] ?? textValue(value)}</Tag>,
    },
    {
      title: '资产地址',
      dataIndex: 'uri',
      key: 'uri',
      width: 360,
      ellipsis: true,
      render: value => (
        <Tooltip title={String(value)}>
          <Typography.Link href={String(value)} target="_blank" rel="noreferrer">{String(value)}</Typography.Link>
        </Tooltip>
      ),
    },
    {
      title: '媒体信息',
      key: 'metadata',
      width: 240,
      render: (_value, row) => {
        const metadata = row.metadata || {};
        const size = metadata.size ?? metadata.file_size ?? metadata.fileSize;
        const width = metadata.width;
        const height = metadata.height;
        const duration = metadata.duration ?? metadata.duration_seconds;
        const values = [
          width && height ? `${width} × ${height}` : null,
          duration ? `时长 ${duration}s` : null,
          size ? `大小 ${size}` : null,
        ].filter(Boolean);
        return values.length ? values.join(' · ') : <Typography.Text type="secondary">未记录媒体参数</Typography.Text>;
      },
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: formatDateTime },
    { title: '资产 ID', dataIndex: 'id', key: 'id', width: 170, render: textValue },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_value, row) => (
        <Space size={4}>
          <ViewButton onClick={() => setDetail(row)}>详情</ViewButton>
          <DownloadButton onClick={() => window.open(row.uri, '_blank', 'noopener,noreferrer')}>打开</DownloadButton>
        </Space>
      ),
    },
  ], []);

  return (
    <div className="project-detail-panel-stack">
      {kindSummary.length ? (
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}>
            <Card size="small" className="management-metric-card"><Statistic title="全部资产" value={resource.total} /></Card>
          </Col>
          {kindSummary.slice(0, 3).map(([kind, count]) => (
            <Col xs={12} md={6} key={kind}>
              <Card size="small" className="management-metric-card">
                <Statistic title={assetKindLabels[kind.toLowerCase()] ?? kind} value={count} />
              </Card>
            </Col>
          ))}
        </Row>
      ) : null}

      <Card title={`资产数据（${resource.total}）`} className="management-list-card">
        <PanelError error={resource.error} onRetry={resource.reload} />
        <DataTable<ProjectAssetRow>
          columns={columns}
          dataSource={resource.items}
          loading={resource.loading}
          rowKey={row => String(row.id)}
          pagination={{ current: resource.page, pageSize: resource.pageSize, total: resource.total }}
          onChange={pagination => {
            const nextPageSize = pagination.pageSize ?? resource.pageSize;
            if (nextPageSize !== resource.pageSize) resource.setPageSize(nextPageSize);
            resource.setPage(pagination.current ?? 1);
          }}
        />
      </Card>

      <DetailDrawer open={Boolean(detail)} title="资产完整信息" onClose={() => setDetail(null)} width={860}>
        {detail ? (
          <div className="project-detail-panel-stack">
            <div className="project-asset-preview project-asset-preview--detail"><AssetPreview asset={detail} detailed /></div>
            <DetailDescriptions
              column={1}
              items={[
                { key: 'id', label: '资产 ID', children: textValue(detail.id) },
                { key: 'project', label: '项目 ID', children: textValue(detail.project_id) },
                { key: 'kind', label: '资产类型', children: assetKindLabels[detail.kind.toLowerCase()] ?? detail.kind },
                { key: 'uri', label: '资产地址', children: <Typography.Paragraph copyable={{ text: detail.uri }}><Typography.Link href={detail.uri} target="_blank">{detail.uri}</Typography.Link></Typography.Paragraph> },
                { key: 'created', label: '创建时间', children: formatDateTime(detail.created_at) },
              ]}
            />
            <Card size="small" title="完整元数据">
              <pre className="project-json-viewer">{jsonText(detail.metadata)}</pre>
            </Card>
          </div>
        ) : null}
      </DetailDrawer>
    </div>
  );
}

interface CanvasBundle {
  loading: boolean;
  error: string | null;
  detail: ProjectCanvasDetail | null;
  nodes: ProjectCanvasNodeRow[];
  connections: ProjectCanvasConnectionRow[];
  groups: ProjectCanvasGroupRow[];
}

export function ProjectCanvasDetailsPanel({ projectId }: { projectId: number }) {
  const navigate = useNavigate();
  const resource = useAdminPage<ProjectCanvasRow>(`/admin/projects/${projectId}/canvases`, 20);
  const [bundles, setBundles] = useState<Record<number, CanvasBundle>>({});
  const [jsonDetail, setJsonDetail] = useState<{ title: string; value: unknown } | null>(null);

  const loadCanvasBundle = useCallback(async (canvasId: number) => {
    if (bundles[canvasId] && !bundles[canvasId].error) return;
    setBundles(current => ({
      ...current,
      [canvasId]: { loading: true, error: null, detail: null, nodes: [], connections: [], groups: [] },
    }));
    try {
      const [detail, nodes, connections, groups] = await Promise.all([
        adminApi.get<ApiResponse<ProjectCanvasDetail>>(`/admin/projects/${projectId}/canvases/${canvasId}`),
        fetchAllAdminPages<ProjectCanvasNodeRow>(`/admin/projects/${projectId}/canvases/${canvasId}/nodes`),
        fetchAllAdminPages<ProjectCanvasConnectionRow>(`/admin/projects/${projectId}/canvases/${canvasId}/connections`),
        fetchAllAdminPages<ProjectCanvasGroupRow>(`/admin/projects/${projectId}/canvases/${canvasId}/groups`),
      ]);
      setBundles(current => ({
        ...current,
        [canvasId]: {
          loading: false,
          error: null,
          detail: detail.data,
          nodes,
          connections,
          groups,
        },
      }));
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === 'REQUEST_ABORTED') return;
      setBundles(current => ({
        ...current,
        [canvasId]: {
          loading: false,
          error: reason instanceof Error ? reason.message : '画布详细信息加载失败',
          detail: null,
          nodes: [],
          connections: [],
          groups: [],
        },
      }));
    }
  }, [bundles, projectId]);

  const nodeColumns = useMemo<TableColumnsType<ProjectCanvasNodeRow>>(() => [
    { title: '节点 ID', dataIndex: 'node_id', key: 'node_id', width: 200, ellipsis: true },
    { title: '节点标题', dataIndex: 'title', key: 'title', width: 180, render: textValue },
    { title: '节点类型', dataIndex: 'type', key: 'type', width: 130, render: value => <Tag>{textValue(value)}</Tag> },
    {
      title: '坐标',
      dataIndex: 'position',
      key: 'position',
      width: 130,
      render: value => {
        const position = value as Record<string, unknown> | null;
        return position ? `${textValue(position.x)}, ${textValue(position.y)}` : '—';
      },
    },
    { title: '布局版本', dataIndex: 'layout_rev', key: 'layout_rev', width: 100, render: formatNumber },
    { title: '内容版本', dataIndex: 'content_rev', key: 'content_rev', width: 100, render: formatNumber },
    { title: '运行版本', dataIndex: 'runtime_rev', key: 'runtime_rev', width: 100, render: formatNumber },
    { title: '生成代次', dataIndex: 'generation_epoch', key: 'generation_epoch', width: 100, render: formatNumber },
    { title: '更新时间', dataIndex: 'update_time', key: 'update_time', width: 180, render: formatDateTime },
    { title: '操作', key: 'action', fixed: 'right', width: 90, render: (_value, row) => <ViewButton onClick={() => setJsonDetail({ title: `节点 ${row.title || row.node_id}`, value: row.node_data })}>数据</ViewButton> },
  ], []);

  const connectionColumns = useMemo<TableColumnsType<ProjectCanvasConnectionRow>>(() => [
    { title: '来源节点', dataIndex: 'source_node_id', key: 'source_node_id', width: 190, ellipsis: true, render: textValue },
    { title: '目标节点', dataIndex: 'target_node_id', key: 'target_node_id', width: 190, ellipsis: true, render: textValue },
    { title: '连线类型', dataIndex: 'connection_type', key: 'connection_type', width: 120, render: value => <Tag>{textValue(value)}</Tag> },
    { title: '来源端口', dataIndex: 'source_handle', key: 'source_handle', width: 140, render: textValue },
    { title: '目标端口', dataIndex: 'target_handle', key: 'target_handle', width: 140, render: textValue },
    { title: '实体版本', dataIndex: 'entity_rev', key: 'entity_rev', width: 100, render: formatNumber },
    { title: '更新时间', dataIndex: 'update_time', key: 'update_time', width: 180, render: formatDateTime },
    { title: '操作', key: 'action', fixed: 'right', width: 90, render: (_value, row) => <ViewButton onClick={() => setJsonDetail({ title: `连线 ${row.connection_key}`, value: row.connection_data })}>数据</ViewButton> },
  ], []);

  const groupColumns = useMemo<TableColumnsType<ProjectCanvasGroupRow>>(() => [
    { title: '分组 ID', dataIndex: 'group_id', key: 'group_id', width: 200, ellipsis: true },
    { title: '分组名称', dataIndex: 'group_label', key: 'group_label', width: 180, render: textValue },
    { title: '节点数', dataIndex: 'group_node_count', key: 'group_node_count', width: 100, render: formatNumber },
    { title: '实体版本', dataIndex: 'entity_rev', key: 'entity_rev', width: 100, render: formatNumber },
    { title: '更新时间', dataIndex: 'update_time', key: 'update_time', width: 180, render: formatDateTime },
    { title: '操作', key: 'action', fixed: 'right', width: 90, render: (_value, row) => <ViewButton onClick={() => setJsonDetail({ title: `分组 ${row.group_label || row.group_id}`, value: row.group_data })}>数据</ViewButton> },
  ], []);

  const canvasColumns = useMemo<TableColumnsType<ProjectCanvasRow>>(() => [
    {
      title: '画布',
      key: 'canvas',
      fixed: 'left',
      width: 230,
      render: (_value, row) => (
        <div className="project-detail-stacked-text">
          <Typography.Text strong>{row.name}</Typography.Text>
          <Typography.Text type="secondary">画布 ID：{row.id}</Typography.Text>
        </div>
      ),
    },
    { title: '版本', dataIndex: 'version', key: 'version', width: 90 },
    { title: '修订号', dataIndex: 'revision', key: 'revision', width: 90, render: formatNumber },
    { title: '节点数', dataIndex: 'nodes_count', key: 'nodes_count', width: 90, render: formatNumber },
    { title: '连线数', dataIndex: 'connections_count', key: 'connections_count', width: 90, render: formatNumber },
    { title: '分组数', dataIndex: 'groups_count', key: 'groups_count', width: 90, render: formatNumber },
    { title: '生成任务数', dataIndex: 'task_count', key: 'task_count', width: 110, render: formatNumber },
    { title: '协作协议', dataIndex: 'collaboration_protocol', key: 'collaboration_protocol', width: 130, render: textValue },
    { title: '最后生成时间', dataIndex: 'last_task_time', key: 'last_task_time', width: 180, render: formatDateTime },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 180, render: formatDateTime },
    { title: '操作', key: 'action', fixed: 'right', width: 100, render: (_value, row) => <ViewButton onClick={() => navigate(`/projects/${projectId}/canvases/${row.id}`)}>独立详情</ViewButton> },
  ], [navigate, projectId]);

  const expandedCanvas = (canvas: ProjectCanvasRow) => {
    const bundle = bundles[canvas.id];
    if (!bundle || bundle.loading) return <div className="project-canvas-loading"><Spin tip="正在加载节点、连线和分组…" /></div>;
    if (bundle.error) return <Alert showIcon type="error" message={bundle.error} action={<a onClick={() => void loadCanvasBundle(canvas.id)}>重试</a>} />;
    return (
      <div className="project-canvas-expanded">
        <Row gutter={[12, 12]}>
          {[
            ['节点', bundle.nodes.length],
            ['连线', bundle.connections.length],
            ['分组', bundle.groups.length],
            ['生成任务', canvas.task_count],
          ].map(([title, value]) => (
            <Col xs={12} md={6} key={String(title)}>
              <Card size="small"><Statistic title={String(title)} value={Number(value)} /></Card>
            </Col>
          ))}
        </Row>
        <Tabs
          items={[
            { key: 'nodes', label: `节点（${bundle.nodes.length}）`, children: bundle.nodes.length ? <DataTable<ProjectCanvasNodeRow> rowKey="id" columns={nodeColumns} dataSource={bundle.nodes} pagination={false} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无节点" /> },
            { key: 'connections', label: `连线（${bundle.connections.length}）`, children: bundle.connections.length ? <DataTable<ProjectCanvasConnectionRow> rowKey="id" columns={connectionColumns} dataSource={bundle.connections} pagination={false} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无连线" /> },
            { key: 'groups', label: `分组（${bundle.groups.length}）`, children: bundle.groups.length ? <DataTable<ProjectCanvasGroupRow> rowKey="id" columns={groupColumns} dataSource={bundle.groups} pagination={false} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无分组" /> },
            { key: 'metadata', label: '画布元数据', children: <pre className="project-json-viewer">{jsonText(bundle.detail?.metadata ?? canvas.metadata)}</pre> },
            {
              key: 'complete-data',
              label: '完整画布数据',
              children: (
                <pre className="project-json-viewer">
                  {jsonText({
                    ...bundle.detail,
                    task_count: canvas.task_count,
                    last_task_time: canvas.last_task_time,
                  })}
                </pre>
              ),
            },
          ]}
        />
      </div>
    );
  };

  return (
    <>
      <Card title={`画布详细信息（${resource.total}）`} className="management-list-card">
        <PanelError error={resource.error} onRetry={resource.reload} />
        <DataTable<ProjectCanvasRow>
          columns={canvasColumns}
          dataSource={resource.items}
          loading={resource.loading}
          rowKey="id"
          pagination={{ current: resource.page, pageSize: resource.pageSize, total: resource.total }}
          onChange={pagination => {
            const nextPageSize = pagination.pageSize ?? resource.pageSize;
            if (nextPageSize !== resource.pageSize) resource.setPageSize(nextPageSize);
            resource.setPage(pagination.current ?? 1);
          }}
          expandable={{
            expandedRowRender: expandedCanvas,
            onExpand: (expanded, row) => {
              if (expanded) void loadCanvasBundle(row.id);
            },
          }}
        />
      </Card>
      <DetailDrawer open={Boolean(jsonDetail)} title={jsonDetail?.title || '完整数据'} onClose={() => setJsonDetail(null)} width={860}>
        <pre className="project-json-viewer">{jsonText(jsonDetail?.value)}</pre>
      </DetailDrawer>
    </>
  );
}
